import { Component, Inject, OnDestroy, Renderer2 } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DeliveryNoteWithDiscrepancies } from '@app/consignee/components/checkin-notes-articles/checkin-notes-articles.component';
import { ToastService } from '@app/core/services/toast.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ModalDialogComponent } from '../modal-dialog/modal-dialog.component';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import {
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-confirmation-dialog',
    templateUrl: './confirmation-dialog.component.html',
    styleUrls: ['./confirmation-dialog.component.scss'],
    imports: [
        ModalDialogComponent,
        MatIcon,
        MatButton,
        MatTooltip,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        CdkCopyToClipboard,
        TranslateModule,
    ],
})
export class ConfirmationDialogComponent implements OnDestroy {
    uncheckedArticles: {
        deliveryNoteNumber: string;
        articles: {
            articleNumber: string;
            description: string;
        }[];
    }[] = [];

    hasUncheckedArticles = false;

    constructor(
        private renderer: Renderer2,
        private toastService: ToastService,
        private translateService: TranslateService,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            msg: string;
            hideBackButton?: boolean;
            icon: string;
            headline: string;
            action: string;
            deliveryNotes?: DeliveryNoteWithDiscrepancies[];
        },
        private dialogRef: MatDialogRef<ConfirmationDialogComponent>
    ) {
        this.renderer.addClass(document.body, 'disable-scroll-and-height');
        this.renderer.addClass(
            document.documentElement,
            'disable-scroll-and-height'
        );

        // Generate list of unchecked articles only if deliveryNotes exist
        if (this.data.deliveryNotes) {
            this.uncheckedArticles = this.data.deliveryNotes
                .map(deliveryNote => {
                    const unchecked = deliveryNote.articles.filter(
                        article => !article.checked
                    );
                    if (unchecked.length > 0) {
                        return {
                            deliveryNoteNumber: deliveryNote.number,
                            articles: unchecked.map(a => ({
                                articleNumber: a.articleNumber,
                                description: a.description,
                            })),
                        };
                    }
                    return null;
                })
                .filter(
                    (item): item is NonNullable<typeof item> => item !== null
                );

            this.hasUncheckedArticles = this.uncheckedArticles.length > 0;
        }
    }

    closeWithResult(result: string): void {
        this.cleanup();
        this.dialogRef.close(result);
    }

    ngOnDestroy() {
        this.cleanup();
    }

    private cleanup(): void {
        this.renderer.removeClass(document.body, 'disable-scroll-and-height');
        this.renderer.removeClass(
            document.documentElement,
            'disable-scroll-and-height'
        );
    }

    /**
     * This function shows a success toast notification indicating that a key has been copied to the clipboard.
     * It uses the `toastService` to display the notification and `translateService` to internationalize the message.
     */
    showKeyCopiedToast() {
        this.toastService.success(
            this.translateService.instant('GENERAL.COPIED-TO-CLIPBOARD')
        );
    }
}
